import React from 'react';
import SharedPageLayout from './layout/SharedPageLayout';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Code2, 
  Key, 
  Server, 
  Lock,
  ArrowRight,
  Copy,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';

const APIReference = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const endpoints = [
    {
      category: 'Authentication',
      icon: Key,
      items: [
        { method: 'POST', path: '/api/auth/login', description: 'Authenticate user and get JWT token' },
        { method: 'POST', path: '/api/auth/register', description: 'Register a new user account' },
        { method: 'POST', path: '/api/auth/refresh', description: 'Refresh authentication token' },
        { method: 'GET', path: '/api/auth/me', description: 'Get current user profile' }
      ]
    },
    {
      category: 'API Testing',
      icon: Code2,
      items: [
        { method: 'GET', path: '/api/api-testing/tests', description: 'List all API tests' },
        { method: 'POST', path: '/api/api-testing/tests', description: 'Create a new API test' },
        { method: 'POST', path: '/api/api-testing/tests/{id}/execute', description: 'Execute an API test' },
        { method: 'GET', path: '/api/api-testing/chains', description: 'List test chains' },
        { method: 'POST', path: '/api/api-testing/chains/{id}/execute', description: 'Execute a test chain' }
      ]
    },
    {
      category: 'Flow Testing',
      icon: Server,
      items: [
        { method: 'GET', path: '/api/flow-testing/tests', description: 'List flow tests' },
        { method: 'POST', path: '/api/flow-testing/tests', description: 'Create a flow test' },
        { method: 'POST', path: '/api/flow-testing/tests/{id}/execute', description: 'Execute a flow test' },
        { method: 'POST', path: '/api/flow-testing/generate', description: 'AI-generate test steps' }
      ]
    },
    {
      category: 'Sprint QA',
      icon: Lock,
      items: [
        { method: 'GET', path: '/api/sprint-qa/projects', description: 'List QA projects' },
        { method: 'GET', path: '/api/sprint-qa/requirements', description: 'List requirements' },
        { method: 'GET', path: '/api/sprint-qa/test-scenarios', description: 'List test scenarios' },
        { method: 'GET', path: '/api/sprint-qa/test-cases', description: 'List test cases' },
        { method: 'POST', path: '/api/sprint-qa/test-executions/start', description: 'Start test execution' }
      ]
    }
  ];

  const getMethodBadge = (method) => {
    const colors = {
      GET: 'bg-green-100 text-green-700',
      POST: 'bg-blue-100 text-blue-700',
      PUT: 'bg-yellow-100 text-yellow-700',
      DELETE: 'bg-red-100 text-red-700',
      PATCH: 'bg-purple-100 text-purple-700'
    };
    return <Badge className={`${colors[method]} font-mono text-xs`}>{method}</Badge>;
  };

  return (
    <SharedPageLayout
      title="API Reference | Lexa.run"
      description="Complete API documentation for Lexa.run. Integrate test automation into your workflows with our REST API."
      onBack={onBack}
      onLogin={onLogin}
      onRegister={onRegister}
      navigateTo={navigateTo}
    >
      {/* Hero Section */}
      <section className="pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
            Developer Resources
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
              API Reference
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Integrate Lexa.run into your CI/CD pipelines and custom workflows.
          </p>
          
          {/* Base URL */}
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-900 rounded-lg text-left">
            <span className="text-gray-400 text-sm">Base URL:</span>
            <code className="text-green-400 font-mono">https://api.lexa.run/api</code>
            <button className="text-gray-400 hover:text-white transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Authentication Notice */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-4">
              <Key className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Authentication Required</h3>
                <p className="text-blue-800 text-sm">
                  Most API endpoints require authentication. Include your JWT token in the Authorization header:
                </p>
                <code className="block mt-2 p-3 bg-blue-100 rounded text-blue-900 text-sm font-mono">
                  Authorization: Bearer YOUR_JWT_TOKEN
                </code>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Endpoints */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {endpoints.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
                </div>
                
                <div className="space-y-3">
                  {category.items.map((endpoint, index) => (
                    <Card key={index} className="p-4 border-gray-200/50 hover:border-purple-200 transition-colors">
                      <div className="flex items-center gap-4">
                        {getMethodBadge(endpoint.method)}
                        <code className="font-mono text-sm text-gray-900 flex-1">
                          {endpoint.path}
                        </code>
                        <span className="text-gray-500 text-sm hidden md:block">
                          {endpoint.description}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Docs CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Need More Details?
          </h2>
          <p className="text-gray-600 mb-6">
            Check out our full API documentation with request/response examples.
          </p>
          <Button 
            onClick={() => navigateTo('/docs')}
            className="rounded-full bg-purple-600 hover:bg-purple-700"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            View Full Documentation
          </Button>
        </div>
      </section>
    </SharedPageLayout>
  );
};

export default APIReference;
